import { Button, Checkbox, confirm, Input, Textarea, toast } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { usePosWarehousesQuery } from "@/modules/cf-feed/presentation/api/queries";
import type {
  DisplaySettings,
  WorkspaceCommand,
  WorkspaceSnapshot,
} from "../../domain/contracts/live-workspace";
import { liveApi } from "../api/client";
import { workspaceRequest } from "../api/workspace-client";
import { LiveBroadcastControls } from "./LiveBroadcastControls";
import { WorkspaceSelect } from "./WorkspaceProductEditor";

type Save = (action: WorkspaceCommand["action"]) => Promise<boolean>;

export function WorkspacePreparation({
  workspace,
  busy,
  save,
  onError,
  onConnected,
}: {
  workspace: WorkspaceSnapshot;
  busy: boolean;
  save: Save;
  onError: (error: unknown) => void;
  onConnected: (feedId: string) => void;
}) {
  const [title, setTitle] = useState(workspace.title);
  const [description, setDescription] = useState(workspace.description ?? "");
  const [warehouseId, setWarehouseId] = useState(workspace.warehouseId ?? "");
  const [reference, setReference] = useState("");
  const [targetFeedId, setTargetFeedId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const createKeysByDraft = useRef(new Map<string, string>());
  const lives = useQuery({
    queryKey: ["workspace-lives", workspace.id],
    queryFn: () =>
      workspaceRequest<
        Array<{
          id: string;
          reference: string;
          title: string | null;
          status: string;
          feedType: "post" | "live";
        }>
      >(`/${workspace.id}/lives`),
    enabled: !workspace.feedId,
    refetchInterval: 15_000,
  });
  const warehouses = usePosWarehousesQuery();
  const dirty =
    title !== workspace.title ||
    description !== (workspace.description ?? "") ||
    warehouseId !== (workspace.warehouseId ?? "");
  const selectedTarget = lives.data?.find((live) => live.id === targetFeedId);
  const connectTarget = async () => {
    if (dirty) {
      toast.error("ກະລຸນາບັນທຶກຂໍ້ມູນງານກ່ອນຜູກ");
      return;
    }
    const targetLabel = selectedTarget
      ? `${selectedTarget.feedType === "live" ? "Live" : "Feed"} · ${selectedTarget.title ?? selectedTarget.reference}`
      : "Facebook Live ທີ່ລະບຸ";
    const accepted = await confirm({
      title: "ຢືນຢັນຜູກງານຂາຍ?",
      description: `${targetLabel}. ລະບົບຈະກວດສະຕັອກ, ລະຫັດ CF, ຕົວເລືອກ ແລະຂໍ້ມູນລ່າສຸດກ່ອນສ້າງງານ. ການຜູກບໍ່ເປີດຮັບ CF ອັດຕະໂນມັດ.`,
      actionText: "ຜູກງານ",
    });
    if (!accepted) return;
    setConnecting(true);
    try {
      const result = await workspaceRequest<{ feedId: string }>(
        `/${workspace.id}/connect`,
        {
          revision: workspace.revision,
          ...(targetFeedId
            ? { feedId: targetFeedId }
            : { liveReference: reference.trim() }),
        },
      );
      toast.success("ຜູກງານຂາຍສຳເລັດ ຍັງບໍ່ເປີດຮັບ CF");
      onConnected(result.feedId);
    } catch (error) {
      onError(error);
    } finally {
      setConnecting(false);
    }
  };
  const createFacebookLive = async () => {
    if (!workspace.draftId) return;
    if (dirty) {
      toast.error("ກະລຸນາບັນທຶກຂໍ້ມູນງານກ່ອນສ້າງ Live");
      return;
    }
    const accepted = await confirm({
      title: "ສ້າງ Facebook Live ໃໝ່?",
      description:
        "ລະບົບຈະສ້າງ Live ໃນ Facebook Page ນີ້ ແລະນຳງານກຽມຂາຍໄປຜູກ. ກ່ອນສ້າງຈະກວດສະຕັອກ, ລະຫັດ CF ແລະຕົວເລືອກ. ຍັງບໍ່ເປີດຮັບ CF ອັດຕະໂນມັດ.",
      actionText: "ສ້າງ Live",
    });
    if (!accepted) return;
    let idempotencyKey = createKeysByDraft.current.get(workspace.draftId);
    if (!idempotencyKey) {
      idempotencyKey = crypto.randomUUID();
      createKeysByDraft.current.set(workspace.draftId, idempotencyKey);
    }
    setConnecting(true);
    try {
      const result = await liveApi.createLive(
        workspace.draftId,
        idempotencyKey,
      );
      toast.success("ສ້າງ Facebook Live ແລະຜູກງານສຳເລັດ");
      onConnected(result.feedId);
    } catch (error) {
      onError(error);
    } finally {
      setConnecting(false);
    }
  };
  return (
    <div className="space-y-5">
      {workspace.feedId && workspace.feedType === "live" && (
        <LiveBroadcastControls feedId={workspace.feedId} />
      )}
      <div className="space-y-3 rounded-lg border p-4">
        <h2 className="font-semibold">1. ກຽມງານກ່ອນມີ Facebook Live ID</h2>
        <output className="text-muted-foreground text-sm">
          {dirty ? "ມີຂໍ້ມູນທີ່ຍັງບໍ່ບັນທຶກ" : "ຂໍ້ມູນງານບັນທຶກແລ້ວ"}
        </output>
        <label htmlFor={"workspace-title"} className="block space-y-1">
          <span className="text-sm">ຊື່ງານ</span>
          <Input
            id={"workspace-title"}
            value={title}
            maxLength={255}
            onChange={(e) => setTitle(e.target.value)}
            className="min-h-11"
          />
        </label>
        <label htmlFor={"workspace-description"} className="block space-y-1">
          <span className="text-sm">ລາຍລະອຽດ</span>
          <Textarea
            id={"workspace-description"}
            value={description}
            maxLength={10000}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <WorkspaceSelect
          label="ສາງທີ່ໃຊ້ຂາຍ"
          value={warehouseId}
          onChange={setWarehouseId}
          items={(warehouses.data?.items ?? []).map((w) => [w.id, w.name])}
        />
        <Button
          className="min-h-11"
          disabled={busy || !title.trim()}
          onClick={() =>
            void save({
              type: "details",
              title,
              description: description || null,
              ...(warehouseId ? { warehouseId } : {}),
            })
          }
        >
          ບັນທຶກຂໍ້ມູນງານ
        </Button>
      </div>
      <div className="space-y-3 rounded-lg border p-4">
        <h2 className="font-semibold">2. ເພີ່ມສິນຄ້າ → ຈັດລຳດັບ → ທົດລອງ OBS</h2>
        <p className="text-muted-foreground text-sm">
          ກຽມລາຄາ ແລະບໍລິການໄດ້ກ່ອນເລີ່ມ live. ທຸກລາຍການບັນທຶກເທິງ server
        </p>
        <Button
          variant="outline"
          className="min-h-11"
          disabled={busy}
          onClick={() =>
            void save({
              type: "copy",
              title: `${workspace.title || "Live"} — ສຳເນົາ`,
            })
          }
        >
          {workspace.feedId ? "ຂາຍຕໍ່ດ້ວຍ Live / Feed ໃໝ່" : "ສຳເນົາເປັນງານໃໝ່"}
        </Button>
        <p className="text-muted-foreground text-xs">
          ສຳເນົາສິນຄ້າ, ລະຫັດ CF, ລາຄາ/ສ່ວນຫຼຸດ, ບໍລິການ, ຄິວ ແລະຈໍ. ງານໃໝ່ຍັງບໍ່ເປີດຮັບ CF
          ຈົນກວ່າຈະກົດເປີດເອງ.
        </p>
      </div>
      <div className="space-y-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">
            3. ຜູກ Facebook Live / Feed ແລ້ວຈຶ່ງເປີດຮັບ CF
          </h2>
          {!workspace.feedId && (
            <output className="rounded-full border px-2 py-1 text-muted-foreground text-xs">
              ຮ່າງ · ຍັງບໍ່ເປີດຮັບ CF
            </output>
          )}
        </div>
        {workspace.feedId ? (
          <p>ຜູກ Live / Feed ແລ້ວ · ລິ້ງ OBS ຍັງເປັນລິ້ງເດີມ</p>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              ເລືອກສິ່ງທີ່ຈະຜູກຈາກ Page ນີ້. ລະບົບຈະກວດຂໍ້ມູນກ່ອນສ້າງງານ ແລະບໍ່ເປີດຮັບ CF
              ອັດຕະໂນມັດ.
            </p>
            <div className="grid gap-2 text-muted-foreground text-xs sm:grid-cols-3">
              <p className="rounded-md bg-muted px-3 py-2">
                ສິນຄ້າທີ່ກຽມ: {workspace.total} ລາຍການ
              </p>
              <p className="rounded-md bg-muted px-3 py-2">
                ສາງ: {workspace.warehouseId ? "ເລືອກແລ້ວ" : "ຍັງບໍ່ເລືອກ"}
              </p>
              <p className="rounded-md bg-muted px-3 py-2">ຈໍ OBS: ບັນທຶກກັບງານນີ້</p>
            </div>
            {lives.data?.length ? (
              <WorkspaceSelect
                label="ເລືອກ Live / Feed ຂອງ Page"
                value={targetFeedId}
                items={lives.data.map((live) => [
                  live.id,
                  `${live.feedType === "live" ? "Live" : "Feed"} · ${live.title ?? live.reference}`,
                ])}
                onChange={(value) => {
                  setTargetFeedId(value);
                  setReference("");
                }}
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                ຖ້າຍັງບໍ່ພົບ live ໃຫ້ກຽມງານຕໍ່ ແລ້ວວາງ ID ເມື່ອມີ
              </p>
            )}
            <Button
              variant="outline"
              className="min-h-11"
              disabled={busy || connecting || dirty || !workspace.draftId}
              onClick={() => void createFacebookLive()}
            >
              ສ້າງ Facebook Live ໃໝ່ຈາກງານນີ້
            </Button>
            <label
              htmlFor={"workspace-live-reference"}
              className="block space-y-1"
            >
              <span className="text-sm">Facebook Live URL / Video ID</span>
              <Input
                id={"workspace-live-reference"}
                value={reference}
                onChange={(e) => {
                  setReference(e.target.value);
                  setTargetFeedId("");
                }}
                className="min-h-11"
              />
            </label>
            <Button
              className="min-h-11"
              disabled={
                busy ||
                connecting ||
                dirty ||
                (!reference.trim() && !targetFeedId)
              }
              onClick={() => void connectTarget()}
            >
              {connecting ? "ກຳລັງຜູກ…" : "ຜູກ Live / Feed"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function WorkspaceDisplaySettings({
  workspace,
  busy,
  save,
}: {
  workspace: WorkspaceSnapshot;
  busy: boolean;
  save: Save;
}) {
  const [settings, setSettings] = useState(workspace.state.settings);
  const [url, setUrl] = useState(() => {
    try {
      return sessionStorage.getItem(`cf-obs-url:${workspace.id}`) ?? "";
    } catch {
      return "";
    }
  });
  const [copied, setCopied] = useState(false);
  const { has } = usePermissions();
  const savedLink = useQuery({
    queryKey: ["workspace-display-token", workspace.id, workspace.tokenActive],
    queryFn: () =>
      workspaceRequest<{ token: string | null }>(
        `/${workspace.id}/display-token`,
      ),
    enabled: has("live:manage") && workspace.tokenActive,
  });
  useEffect(() => {
    if (savedLink.data?.token)
      setUrl(
        `${window.location.origin}/overlay/${workspace.id}#token=${savedLink.data.token}`,
      );
  }, [savedLink.data?.token, workspace.id]);
  const booleanFields: Array<[keyof DisplaySettings, string]> = [
    ["image", "ຮູບສິນຄ້າ"],
    ["title", "ຊື່ສິນຄ້າ"],
    ["codes", "ລະຫັດ CF"],
    ["prices", "ລາຄາກ່ອນ–ຫຼັງຫຼຸດ"],
    ["remaining", "ສະຕັອກຄົງເຫຼືອ"],
    ["orders", "ອໍເດີລ່າສຸດ 5 ລາຍການ"],
    ["totals", "ຍອດຮັບ CF ລວມ"],
    ["next", "ສິນຄ້າຖັດໄປ"],
  ];
  return (
    <div className="space-y-4">
      <h2 className="font-semibold">ຂໍ້ມູນທີ່ຜູ້ຊົມເຫັນ</h2>
      <p className="text-muted-foreground text-sm">
        ສີ ແລະຮູບແບບໃຊ້ຄ່າກາງຮ່ວມກັນທຸກ Live / Feed.{" "}
        <a
          href="/app/widget-settings"
          className="inline-flex min-h-11 items-center underline"
        >
          ໄປຕັ້ງຄ່າ Widget ສ່ວນກາງ
        </a>
      </p>
      <div className="grid grid-cols-2 gap-2">
        {booleanFields.map(([key, label]) => (
          <label
            htmlFor={`workspace-display-${workspace.id}-${key}`}
            key={key}
            className="flex min-h-11 items-center gap-2 text-sm"
          >
            <Checkbox
              id={`workspace-display-${workspace.id}-${key}`}
              checked={settings[key] === true}
              onCheckedChange={(value) =>
                setSettings({ ...settings, [key]: value === true })
              }
            />
            {label}
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <WorkspaceSelect
          label="ຊື່ຜູ້ຊື້"
          value={settings.buyerName}
          items={[
            ["hidden", "ປິດ"],
            ["short", "ຊື່ຫຍໍ້"],
            ["full", "ຊື່ເຕັມ"],
          ]}
          onChange={(v) =>
            setSettings({
              ...settings,
              buyerName: v as DisplaySettings["buyerName"],
            })
          }
        />
        <WorkspaceSelect
          label="ອໍເດີທີ່ສະແດງ"
          value={settings.orderMode}
          items={[
            ["accepted", "ຮັບ CF ສຳເລັດ"],
            ["paid", "ຊຳລະແລ້ວຈາກ Admin"],
          ]}
          onChange={(v) =>
            setSettings({
              ...settings,
              orderMode: v as DisplaySettings["orderMode"],
            })
          }
        />
        <WorkspaceSelect
          label="ພື້ນຫຼັງ"
          value={settings.background}
          items={[
            ["transparent", "ໂປ່ງໃສ"],
            ["solid", "ພື້ນທຶບ"],
          ]}
          onChange={(v) =>
            setSettings({
              ...settings,
              background: v as DisplaySettings["background"],
            })
          }
        />
        <WorkspaceSelect
          label="ຮູບແບບ Widget"
          value={settings.orientation}
          items={[
            ["landscape", "ແນວນອນ"],
            ["portrait", "ແນວຕັ້ງ"],
          ]}
          onChange={(v) =>
            setSettings({
              ...settings,
              orientation: v as DisplaySettings["orientation"],
            })
          }
        />
      </div>
      <Button
        className="min-h-11"
        disabled={busy}
        onClick={() => void save({ type: "settings", settings })}
      >
        ບັນທຶກການຕັ້ງຈໍ
      </Button>
      <div className="space-y-3 rounded-lg border p-4">
        <h3 className="font-semibold">ລິ້ງສຳລັບ OBS Browser Source</h3>
        <p className="text-muted-foreground text-sm">
          OBS → Sources → Browser → ວາງລິ້ງ. ເລີ່ມທີ່ Width 800 / Height 600
          ແລ້ວປັບຂະໜາດ, ຕັດຂອບ ແລະລາກ Widget ໄປຕຳແໜ່ງທີ່ຕ້ອງການໃນ OBS.
        </p>
        {url && workspace.tokenActive && (
          <>
            <Input
              aria-label="OBS URL"
              readOnly
              value={url}
              className="min-h-11"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="min-h-11"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(url);
                    setCopied(true);
                  } catch {
                    setCopied(false);
                  }
                }}
              >
                {copied ? "ຄັດລອກແລ້ວ" : "ຄັດລອກລິ້ງ"}
              </Button>
              <a
                className="inline-flex min-h-11 items-center underline"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                ເປີດຈໍທົດລອງ
              </a>
            </div>
          </>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="min-h-11"
            disabled={busy}
            onClick={async () => {
              const token = Array.from(
                crypto.getRandomValues(new Uint8Array(32)),
                (value) => value.toString(16).padStart(2, "0"),
              ).join("");
              if (await save({ type: "token", token })) {
                const url = `${window.location.origin}/overlay/${workspace.id}#token=${token}`;
                setUrl(url);
                try {
                  sessionStorage.setItem(`cf-obs-url:${workspace.id}`, url);
                } catch {}
              }
            }}
          >
            {workspace.tokenActive ? "ສ້າງລິ້ງໃໝ່ (ລິ້ງເກົ່າຢຸດໃຊ້)" : "ສ້າງລິ້ງ OBS"}
          </Button>
          <Button
            variant="destructive"
            className="min-h-11"
            disabled={busy || !workspace.tokenActive}
            onClick={async () => {
              if (await save({ type: "revoke" })) {
                setUrl("");
                try {
                  sessionStorage.removeItem(`cf-obs-url:${workspace.id}`);
                } catch {}
              }
            }}
          >
            ຍົກເລີກລິ້ງ
          </Button>
        </div>
        <p className="text-muted-foreground text-xs">
          ລິ້ງນີ້ໃຫ້ອ່ານຈໍຂອງງານນີ້ເທົ່ານັ້ນ. ບໍ່ສົ່ງເບີໂທ ທີ່ຢູ່ ຫຼືຂໍ້ມູນຊຳລະເງິນຂຶ້ນຈໍ
        </p>
      </div>
    </div>
  );
}
